import { Form, Formik } from "formik";
import React, { FC, ReactElement, useEffect, useState } from "react";
import Modal from 'react-modal';
import { useLocale } from "../../../contexts/localeContext.tsx";
import { TranslationKeys } from "../../../contexts/TranslationKeys.ts";
import WizardSideBarStep from "../../../models/WizardSideBarStep.ts";
import WizardTwo from "../../wizard/wizardTwo.tsx";
import SummaryStep from "./SummaryStep.tsx";
import DetailStep from "./DetailStep.tsx";
import { TableColumn } from "@/models/table/TableColumn.ts";
import { SearchTerm } from "@/models/search/SearchTerm.ts";
import { ASSIGNING_TYPE } from "@/constants/expiry-reason-constant.ts";
import AssignStep from "./AssignStep.tsx";
import { Lookup } from "@/models/Lookup.ts";

interface MasterExpiryModalProps {
    dataMethod: (listParameter?: any) => Promise<any>;
    modalTitle: string;
    assigningTitle: string;
    tableColumns: TableColumn[];
    searchColumns: SearchTerm[];
    isOpen: boolean;
    onClose: () => void;
    getAssignedItems: (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>) => void;
    handleSubmit: (toBeAssigned: string[], toBeAssignedRows: any[], toBeUnassigned: string[], values: any) => void;
    assigningType: ASSIGNING_TYPE
    idPath?: string;
    lookup?: Lookup[];
}

const MasterExpiryModal: FC<MasterExpiryModalProps> = ({ dataMethod, modalTitle, assigningTitle, tableColumns, searchColumns, isOpen, onClose, getAssignedItems, assigningType, idPath = "id", handleSubmit, lookup }) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [totalSelected, setTotalSelected] = useState<number>(0);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [originalSelectedIds, setOriginalSelectedIds] = useState<string[]>([]);
    const [originalSelectedRows, setOriginalSelectedRows] = useState<any[]>([]);
    const toBeAssigned = selectedIds.filter(id => !originalSelectedIds.includes(id));
    const initialValues = {
        selectedIds: [],
        selectedRows: [],
        useSameDate: false
    }
    const { translate } = useLocale();
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        if (isOpen) {
            getAssignedItems(setSelectedIds, setSelectedRows, setOriginalSelectedIds, setOriginalSelectedRows);
        }
    }, [isOpen]);

    const steps: WizardSideBarStep[] = [
        {
            title: assigningTitle,
            description: totalSelected !== 0 ? translate.byKeyFormatted(TranslationKeys.selectedWithCount, [totalSelected]) : translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        ...(toBeAssigned.length > 0 ?[
            {
            title: translate.byKey(TranslationKeys.details),
            description: '',
            substeps: []
        }
        ] : []),
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const onLocalNext = (event: any) => {
        if (currentStep === 0) {
            const toBeAssigned = selectedIds.filter(id => !originalSelectedIds.includes(id));
            if (toBeAssigned.length === 0) {
                setCurrentStep(2);
                return;
            }
        }

        setCurrentStep(currentStep + 1);
    }

    const onLocalBack = (event: any) => {
        if (currentStep === 2) {
            const toBeAssigned = selectedIds.filter(id => !originalSelectedIds.includes(id));
            if (toBeAssigned.length === 0) {
                setCurrentStep(0);
                return;
            }
        }

        setCurrentStep(currentStep - 1);
    }

    const stepContent: ReactElement[] = [
        <AssignStep
            key={`1`}
            dataMethod={dataMethod}
            assigningTitle={assigningTitle}
            onNext={onLocalNext}
            selectedIds={selectedIds}
            selectedRows={selectedRows}
            tableColumns={tableColumns}
            searchColumns={searchColumns}
            setSelectedIds={setSelectedIds}
            setSelectedRows={setSelectedRows}
            originalSelectedIds={originalSelectedIds}
            setTotalSelected={setTotalSelected} 
            lookup={lookup} />,
        <DetailStep key={'2'}
            assigningType={assigningType}
            idPath={idPath} 
            originalSelectedRows={originalSelectedRows}
            onBack={onLocalBack} 
            onNext={onLocalNext}/>,
        <SummaryStep
            key={`3`}
            idPath={idPath} 
            originalSelectedRows={originalSelectedRows} 
            assigningType={assigningType}
            onBack={onLocalBack} />
    ]

    const handleClose = () => {
        setCurrentStep(0);
        setTotalSelected(0);
        onClose();
    }

    const onSubmit = (values: any) => {
        const toBeAssigned = values.selectedIds.filter((id: string) => !originalSelectedIds.includes(id));
        const toBeAssignedRows = selectedRows.filter((row: any) => !originalSelectedRows.some((original: any) => original[idPath] === row.id.toString()));
        const toBeUnassignedIds = originalSelectedRows.filter((original: any) => !selectedRows.some((row: any) => row.id.toString() === original[idPath])).map((original: any) => original.id);
        handleSubmit(toBeAssigned, toBeAssignedRows, toBeUnassignedIds, values);
        handleClose();
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none w-content"
        data={{cy:"wizardModal"}}>
        <Formik
            key={String(isOpen)}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={onSubmit}>
            <Form>
                <WizardTwo
                    name={modalTitle}
                    step={currentStep}
                    onClose={handleClose}
                    sidebarSteps={steps}
                    stepContent={stepContent} />
            </Form>
        </Formik>
    </Modal>
}

export default MasterExpiryModal;